import React, { useState } from 'react';

import css from './AddOnsSelector.module.css';

/**
 * Add-Ons selector shown in the booking panel when the listing has add-ons configured.
 */
const AddOnsSelector = ({ addOns, selectedAddons, setSelectedAddons }) => {
  const [showAddons, setShowAddons] = useState(false);
  if (!addOns || addOns.length === 0) return null;

  return (
    <div className={css.addOnsSection}>
      <button
        type="button"
        onClick={() => setShowAddons(prev => !prev)}
        style={{
          border: '1px solid #d0d0d0',
          borderRadius: '8px',
          padding: '10px 16px',
          width: '100%',
          textAlign: 'left',
          background: 'white',
          cursor: 'pointer',
          fontSize: '14px',
          color: '#444',
        }}
      >
        {showAddons ? '− Hide extras' : '+ Add extras'}
      </button>
      {showAddons ? <div style={{ marginTop: '16px' }}>
      {addOns.map(addon => {
        if (addon.type === 'pool') {
          const items = Array.isArray(selectedAddons[addon.id]) ? selectedAddons[addon.id] : [];
          const addlPrice = (addon.additionalPrice / 100).toFixed(2);
          const freeCount = addon.freeCount || 0;
          const isActive = items.length > freeCount;

          const handleItemChange = (index, val) => {
            const updated = [...items];
            updated[index] = val;
            setSelectedAddons(prev => ({ ...prev, [addon.id]: updated }));
          };
          const handleAddPoolItem = () => {
            if (items.length < addon.maxItems) {
              setSelectedAddons(prev => ({
                ...prev,
                [addon.id]: [...items, addon.itemOptions?.[0] || ''],
              }));
            }
          };
          const handleRemovePoolItem = index => {
            setSelectedAddons(prev => ({
              ...prev,
              [addon.id]: items.filter((_, i) => i !== index),
            }));
          };

          return (
            <div
              key={addon.id}
              className={`${css.addOnCard} ${css.addOnCardPool} ${isActive ? css.addOnCardActive : ''}`}
            >
              <div className={css.addOnCardLeft}>
                <span className={css.addOnCardName}>
                  {addon.name}
                  {addon.required ? (
                    <span className={css.addonRequiredBadge}>Required</span>
                  ) : null}
                </span>
                {addon.description ? (
                  <span className={css.addOnCardDesc}>{addon.description}</span>
                ) : null}
                <span className={css.addOnCardPriceNote}>
                  First {freeCount} item{freeCount !== 1 ? 's' : ''} included free · +${addlPrice}{' '}
                  each additional
                </span>
              </div>
              <div className={css.poolSlotsFullWidth}>
                {items.map((item, index) => {
                  const isFree = index < freeCount;
                  return (
                    <div key={index} className={css.poolSlot}>
                      <select
                        className={css.poolSlotSelect}
                        value={item}
                        onChange={e => handleItemChange(index, e.target.value)}
                      >
                        {(addon.itemOptions || []).map(opt => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      <span className={css.poolSlotPrice}>
                        {isFree ? '$0.00 — Included' : `$${addlPrice}`}
                      </span>
                      <button
                        type="button"
                        className={css.poolSlotRemove}
                        onClick={() => handleRemovePoolItem(index)}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
                {items.length < addon.maxItems ? (
                  <button
                    type="button"
                    className={css.poolAddItemButton}
                    onClick={handleAddPoolItem}
                  >
                    + Add item
                  </button>
                ) : null}
              </div>
            </div>
          );
        }

        // Standard add-on
        const qty = selectedAddons[addon.id] || 0;
        const isRequired = !!addon.required;
        const freeQty = addon.freeQuantity || 0;
        const priceDollars = (addon.price / 100).toFixed(2);
        const priceDisplay =
          addon.price > 0 && freeQty > 0
            ? `First ${freeQty} included · +$${priceDollars} ea`
            : addon.price > 0
            ? `+$${priceDollars}`
            : 'Included';
        const isActive = qty > 0;

        const handleQtyChange = newQty => {
          if (isRequired && newQty < 1) return;
          setSelectedAddons(prev => ({ ...prev, [addon.id]: newQty }));
        };

        return (
          <div key={addon.id} className={`${css.addOnCard} ${isActive ? css.addOnCardActive : ''}`}>
            <div className={css.addOnCardLeft}>
              <span className={css.addOnCardName}>
                {addon.name}
                {isRequired ? <span className={css.addonRequiredBadge}>Required</span> : null}
              </span>
              {addon.description ? (
                <span className={css.addOnCardDesc}>{addon.description}</span>
              ) : null}
            </div>
            <div className={css.addOnCardRight}>
              {addon.maxQuantity === 1 ? (
                <label className={css.addonCheckboxLabel}>
                  <input
                    type="checkbox"
                    className={css.addonCheckbox}
                    checked={qty > 0}
                    disabled={isRequired}
                    onChange={e => handleQtyChange(e.target.checked ? 1 : 0)}
                  />
                </label>
              ) : (
                <div className={css.addonQtySelector}>
                  <button
                    type="button"
                    className={css.addonQtyButton}
                    disabled={isRequired ? qty <= 1 : qty <= 0}
                    onClick={() => handleQtyChange(Math.max(isRequired ? 1 : 0, qty - 1))}
                  >
                    −
                  </button>
                  <span className={css.addonQtyValue}>{qty}</span>
                  <button
                    type="button"
                    className={css.addonQtyButton}
                    disabled={qty >= addon.maxQuantity}
                    onClick={() => handleQtyChange(Math.min(addon.maxQuantity, qty + 1))}
                  >
                    +
                  </button>
                </div>
              )}
              <span className={css.addOnCardPrice}>{priceDisplay}</span>
            </div>
          </div>
        );
      })}
      </div> : null}
    </div>
  );
};

export default AddOnsSelector;

/**
 * Convert the component's keyed-object `selectedAddons` state into the array
 * shape stored on the wire (protectedData.selectedAddons). Omits zero-quantity
 * standard addons and empty-items pool addons so the wire shape only contains
 * actually-selected entries.
 *
 * Signature note: originally an inline closure in OrderPanel that captured
 * `addOns` lexically. Now takes `addOns` as an explicit first parameter so it
 * can live standalone.
 *
 * @param {Array} addOns — the listing's publicData.add_ons
 * @param {Object} currentSelected — keyed-object state from AddOnsSelector
 * @returns {Array} filtered wire-shape array
 */
export const buildActiveAddons = (addOns, currentSelected) =>
  addOns.flatMap(addon => {
    if (addon.type === 'pool') {
      const items = Array.isArray(currentSelected[addon.id])
        ? currentSelected[addon.id].filter(Boolean)
        : [];
      if (items.length === 0) return [];
      const totalPrice =
        Math.max(0, items.length - (addon.freeCount || 0)) * (addon.additionalPrice || 0);
      return [
        {
          id: addon.id,
          type: 'pool',
          name: addon.name,
          items,
          freeCount: addon.freeCount || 0,
          additionalPrice: addon.additionalPrice || 0,
          totalPrice,
        },
      ];
    }
    const qty = currentSelected[addon.id] || 0;
    if (qty <= 0) return [];
    return [
      {
        id: addon.id,
        type: 'standard',
        name: addon.name,
        price: addon.price,
        quantity: qty,
        freeQuantity: addon.freeQuantity || 0,
      },
    ];
  });

/**
 * Reverse transform: convert a wire-shape array back into the component's
 * keyed-object state. Used by consumers (e.g., ModifyBookingModal in Push
 * 7A.3 Phase 3) that read `protectedData.selectedAddons` from a tx and need
 * to populate the AddOnsSelector's initial state.
 *
 * Behavior:
 * - If `activeArray` is null/undefined, treats it as empty (falls through to
 *   initial-state defaults for each addon).
 * - For each addon def in `addonDefs`:
 *   • Pool: use the matching active entry's `items` array if present, padded
 *     to at least `freeCount` with copies of `itemOptions[0]` (or '' if no
 *     options). If no matching entry, returns `Array(freeCount).fill(firstOption)`.
 *     Matches the initial-state seeding logic in OrderPanel.
 *   • Standard: use the matching entry's `quantity` if present, else
 *     `addon.required ? 1 : 0`.
 * - Active entries whose id is not in `addonDefs` are silently dropped
 *   (handles the edge case where a listing edit removed an addon after a
 *   booking was made).
 *
 * Round-trip: `buildActiveAddons(defs, fromActiveAddons(defs, arr))` produces
 * a content-equivalent array to `arr` (order-independent) for any `arr`
 * previously produced by `buildActiveAddons`.
 *
 * @param {Array} addonDefs — the listing's publicData.add_ons
 * @param {Array|null|undefined} activeArray — the wire-shape array
 * @returns {Object} keyed-object state: { [addonId]: number | string[] }
 */
export const fromActiveAddons = (addonDefs, activeArray) => {
  const defs = Array.isArray(addonDefs) ? addonDefs : [];
  const active = Array.isArray(activeArray) ? activeArray : [];
  const byId = new Map(active.map(entry => [entry.id, entry]));
  const result = {};
  defs.forEach(addon => {
    const entry = byId.get(addon.id);
    if (addon.type === 'pool') {
      const freeCount = addon.freeCount || 0;
      const firstOption = addon.itemOptions?.[0] || '';
      if (entry && Array.isArray(entry.items)) {
        const items = [...entry.items];
        while (items.length < freeCount) {
          items.push(firstOption);
        }
        result[addon.id] = items;
      } else {
        result[addon.id] = Array(freeCount).fill(firstOption);
      }
    } else if (entry && typeof entry.quantity === 'number') {
      result[addon.id] = entry.quantity;
    } else {
      result[addon.id] = addon.required ? 1 : 0;
    }
  });
  return result;
};
